import { Config } from 'config';

import { forEach, map, find } from 'lodash';

import { cloneDeep, map$, filterRight } from 'gear';

import { Point,
         Document, Figures,
         FigureConditions_Mixin } from 'cdl';

import { View, Plugin, SyncedFigureViews,
         View2D_Mixin, ViewTransform,
         DocumentView_Mixin,
         SelectedFigures_Mixin,
         InteractiveView_Mixin  } from '../view';

import '../plugin/active-renderer';
import '../plugin/special-pens';
import '../plugin/alignment-lines';
import '../plugin/figure-annotations';


export const StaticCanvas = View.extend( View2D_Mixin )
                                .extend( DocumentView_Mixin )
                                .extend( SelectedFigures_Mixin )
                                .extend( FigureConditions_Mixin ).extend({

  typeName: 'StaticCanvas',

  Properties: {

    mainDocument: { type: 'Document', def: null, owned: false, set( v ) { this._setDoc(v); } },

    background: { type: 'Document', def: null, owned: false, set( v ) { this._setBackground(v); } },

    allowMultiDocumentSelection: { type: 'boolean', def: false },

  },
  postResize() {
    if (Config.keepLowerLeftCornerAsOrigin) {
      this.centerPages(this.viewCenter);
    }
  },
  get document() {
    return this._document;
  },
  set document( doc ) {
    this._setDoc(doc);
  },

  zoomToFit$(/* ...params */) {
    return this.zoomToFitFigures$(...arguments);
  },

  resetToOrigin() {
    this.zoomPages(1);
    this.centerPages(this.viewCenter);
  },
  // [TODO] Allow apps to better configure addition using general filters

  add( figures, config ) {
    const selector = ( config && config.layer ) || this.destinationLayerSelector;
    if( selector ) {
      this.setActiveLayerBy(selector);
    }
    return this.callParent(figures,config);
  },

  // Allow Multi Document Selection
  figureAtPointEx( docPoint, tabFigures ) {
    if( this._allowMultiDocumentSelection && ! this._document.editingContent() ) {
      return this.topFigureFromEx( this.activeSelectableFiguresForDoc(this._mainDocument), docPoint, tabFigures ) ||
             this.topFigureFromEx( this.activeSelectableFiguresForDoc(this._background), docPoint, tabFigures );
    }
    else {
      return this.callParent(docPoint,tabFigures);
    }
  },

  getBrushAtPoint( docPoint ) {
    let lFigure = this.brushHitTest( this.activeSelectableFiguresForDoc(this._mainDocument), docPoint) ||
                  this.brushHitTest( this.activeSelectableFiguresForDoc(this._background), docPoint );

    if (lFigure != null && lFigure.tight) 
    {
      if (lFigure.polyregion != null && lFigure.polyregion.brush != null && !lFigure.polyregion.brush.empty())
        return lFigure.polyregion.brush;

        return lFigure.figure?lFigure.figure.brush:null;
    }
     
     return null;
  },

  brushHitTest( fromFigures, docPoint ) {
    const figures = filterRight( fromFigures, figure => {
      return figure.frame_().contains(docPoint);
    });
    const candidates = map( figures, figure => {
      return { figure, docPoint, tight: false, polyregion:null };
    });
    return this.brushPerformHitTest(candidates);
  },

  brushPerformHitTest( candidates ) {
    if( candidates.length === 0 ) {
      return null;       // Nothing around
    }
    const candidate = this.brushCheckCandidates( candidates );
    if( candidate ) {
      candidate.tight = true;
      return candidate;
    }
    return null;
  },

  brushCheckCandidates( candidates ) {
    for( let k = 0, kEnd = candidates.length; k < kEnd; ++k ) {
      const candidate = candidates[k];
      const lOptions = {frameBased:false};
      if( this.figureView(candidate.figure).hitTest(candidate.docPoint, lOptions) ) {
        candidate.polyregion = lOptions.polyregionHit;
        return candidate;
      }
    }
  },


  setActiveLayerBy( selector ) {
    let doc = this._mainDocument,
        layer = doc.activePage.layerBy(selector);
    if( ! layer ) {
      doc = this._background;
      layer = doc.activePage.layerBy(selector);
    }

    if( layer ) {
      this.activeDocument = doc;
      this.activeLayer = layer;
    }
    return this;
  },

  // [TODO] insertViewPlugins

  drawPage( page, context, transform, config ) {
    page.forEachFigure( figure => {
      this.figureView(figure).draw(context,transform,config);
    });
  },

  redrawFigure( figure ) {
    this.renderer.redrawFigure(this,figure);
  },

  mapToFigureViews( figures ) {
    return this.figureViews.mapToFigureViews(figures);
  },

  figureView( figure ) {
    return this.figureViews.get(figure);
  },

  init( config = {} ) {

    this.addEvents(
      'brushselected',
      'activedocumentchanged',
      'newdocument',
      'newbackground'
    );

    this._bind('zoomToFit$')
        ._bind('zoomToFitPages$');

    this.figureViews = new SyncedFigureViews({ view: this });

    this.pageViewTransforms = new WeakMap();

    // Broadcast the first view for debugging
    const Debug = Config.Debug;
    if( Debug && ! Debug.canvas ) {
      Debug.canvas = this;
    }

    this.callParent(config);

    // Keep this at the end of the init function, because events are triggered
    // that needs all Mixins to be already initialized
    this._setBackground( new Document() );
    this._setDoc( new Document() );
  },


  // Intiatilization of documents and DocumentView interaction
  //----------------------------------------------------------------------------
  // The docs are linked to the figureViews here, the active document events
  // are linked directly in activeDocument

  get activeDocument() {
    return this._document;
  },
  set activeDocument( doc ) {
    const oldDoc = this._document;
    if( oldDoc !== doc ) {
      if( oldDoc ){
        this.unlinkDoc(oldDoc);
      }

      this._activeDocumentChanged = true;

      // assert( v === this._mainDocument || v === this._background );
      this._document = doc;
      this.selectedFigures._clear();

      this.linkDoc(doc);
      this.fireEvent('activedocumentchanged',doc);
    }
  },

  otherDocFor( doc ) {
    return doc === this._mainDocument ? this._background : this._mainDocument;
  },

  otherDocPageFor( page ){
    return page.document === this.mainDocument ? this.backgroundPageFor(page)
                                               : this.mainDocumentPageFor(page);
  },

  fireChangeEvents( type, doc ) {
    const { _activeDocumentChanged } = this;
    this._activeDocumentChanged = false;

    return this.callParent(type,doc) || _activeDocumentChanged;
  },

  _setDoc( doc ) {
    const oldDoc = this._mainDocument;
    if( oldDoc ) {
      this._unlinkMainDoc(oldDoc);
    }
    this._mainDocument = doc;
    // Keep the main document as the active document
    if( ! this._document || this._document === oldDoc ) {
      this.activeDocument = doc;
    }
    this._linkMainDoc(doc);
    if (Config.keepLowerLeftCornerAsOrigin) {
      this.centerPages(this.viewCenter);
    }
    this.fireEvent('newdocument',this,doc,oldDoc);
  },
  _linkMainDoc( doc ) {
    this.figureViews.link(doc);
    this.on_pageschanged(doc,doc.pages);
    this.on_activepagechanged(doc,doc.activePage);
  },
  _unlinkMainDoc( doc ) {
    this.figureViews.unlink(doc);
  },


  _setBackground( doc ) {
    const oldDoc = this._background;
    if( oldDoc ) {
      this._unlinkBackground(oldDoc);
    }
    this._background = doc;
    this._linkBackground(doc);
    this.fireEvent('newbackground',this,doc,oldDoc);
  },
  _linkBackground( doc ) {
    this.figureViews.link(doc);
  },
  _unlinkBackground( doc ) {
    this.figureViews.unlink(doc);
  },

  dispose() {
    this._unlinkBackground(this.background);
    this._unlinkMainDoc(this.mainDocument);
    this.callParent();
  },

  redrawDoc() {
    this.redraw('document','background'); // [TODO] Hack for demo, not needed

    /*
    if( this.doc === this.mainDocument ) {
      this.redraw('document');
    }
    else {
      this.redraw('background');
    }
    */
  },

  // Syncronization between mainDocument and background
  //---------------------------------------------------------------------------------------

  on_rendercomplete( doc ) {
    // If the document was commited, also issue a render of the other doc because Canvas
    // promises that everything is going to get rendered after that (if not we could have
    // bugs with patterns like change doc > zoom to fit doc > doc.commit$
    this.otherDocFor(doc).render$();
    this.callParent(...arguments);
  },

  on_activepagechanged( doc, page ) {
    this.otherDocFor(doc).activePage = this.otherDocPageFor(page);
    this.viewTransform = this.pageViewTransform(this.mainDocument.activePage);
    this.callParent(doc,page);
  },

  // [TODO] Add options for different background/mainDocument setups
  //        Avoid assuming an structure. Directly provide Apps configuration
  //        points to decide how pages are syncronized. For some applications
  //        the background pages may be totally independent of the main document

  backgroundPageFor( page ) {
    const background = this._background;
    const backgroundPages = background._pages;
    const pageId = page._id;

    let backgroundPage = pageId !== null ? find( backgroundPages, { id: page.id } ) : null;
    if( ! backgroundPage ) {
      const pages = this._mainDocument._pages;
      backgroundPage = backgroundPages[Math.min(pages.indexOf(page),backgroundPages.length-1)];
    }
    return backgroundPage;
  },

  mainDocumentPageFor( page ) {
    const mainDocument = this._mainDocument;
    const mainDocumentPages = mainDocument._pages;
    const pageId = page._id;

    let mainDocumentPage = pageId !== null ? find( mainDocumentPages, { id: page.id } ) : null;
    if( ! mainDocumentPage ) {
      const pages = this._background._pages;
      mainDocumentPage = mainDocumentPages[Math.min(pages.indexOf(page), mainDocumentPages.length-1)];
    }
    return mainDocumentPage;
  },

  // Page independent view transforms
  //--------------------------------------------------------------------------------

  // [TODO] We could use getState/setState to store the viewTransform for each page

  getZoomToFitFigures() {
    return this.visibleCrossDocumentPageFiguresOf(this.activePage);
  },

  visibleCrossDocumentPageFiguresOf( page ) {
    const figures = page.visibleFigures;
    const backgroundPage = this.backgroundPageFor(page);
    return backgroundPage ? figures.concat( backgroundPage.visibleFigures ) : figures;
  },

  zoomToFitPages$( margin = this._defaultMargin() ) {
    return map$(this._mainDocument._pages, page => {
      const figures = this.visibleCrossDocumentPageFiguresOf(page);
      return Figures.bounds$(figures).then( bounds => {
      this.pageViewTransform(page).zoomAround(this,bounds,margin);
      });
    }).then( () => this.on_zoomupdated() );
  },

  zoomPages( zoom ) {
    forEach(this.mainDocument.pages, page => {
      this.pageViewTransform(page).zoom = zoom;
    });
    this.on_zoomupdated();
    return this;
  },

  centerPages( center ) {
    forEach( this._mainDocument._pages, page => {
      this.pageViewTransform(page).center = new Point( center );
    });
    this.on_zoomupdated();
    return this;
  },

  pageViewTransform( page ) {
    let viewTransform = this.pageViewTransforms.get(page);
    if( ! viewTransform ){
      viewTransform = this.pageViewTransforms.get( this.otherDocPageFor(page) );
    }
    return viewTransform;
  },

  setPageViewTransform( page, viewTransform ) {
    this.pageViewTransforms.set(page,viewTransform);
  },

  // [TODO]
  // This can be improved by linking pages when commiting ( undoPage, redoPage links )
  // That will allow to even preserve zooming when adding or deleting pages
  // and could simplify a lot of this code

  on_pageschanged( doc, added, removed ) {
    if( added ) {
      forEach( added, page => {
        if( ! this.pageViewTransform(page) ){
          this.setPageViewTransform(page,new ViewTransform());
        }
      });
    }
    if( removed ) {
      forEach( removed, page => this.pageViewTransforms.delete(page) );
    }
    this.callParent(doc,added,removed);
  },

  on_historycommitstarted( doc, waitFor, type ) {
    this._undoRedoViewTransform = cloneDeep(
      map( doc.pages, page => this.pageViewTransform(page) )
    );
    this.callParent(doc,waitFor,type);
  },

  on_undo( doc ){
    this.onUndoRedoRevert(doc);
    this.callParent(...arguments);
  },
  on_redo( doc ){
    this.onUndoRedoRevert(doc);
    this.callParent(...arguments);
  },
  on_revert( doc ){
    this.onUndoRedoRevert( doc );
    this.callParent(...arguments);
  },

  onUndoRedoRevert( doc ) {
    const { pages } = doc;
    if( this._undoRedoViewTransform.length === pages.length ) {

      // Pages have not change, maintain zoom for them
      forEach( pages, (page,k) => {
        this.setPageViewTransform( page, this._undoRedoViewTransform[k] );
      });

      this.viewTransform = this.pageViewTransform(doc.activePage);
      this._undoRedoViewTransform = [];

      this.render$();
    }
    else {
      this.zoomToFitPages$().then(this.render$);
    }
  },

});


export const Canvas = StaticCanvas.extend( InteractiveView_Mixin ).extend({

  typeName: 'Canvas',

  Properties: {

    // [TODO] Use conditions
    // dblclickContentEditionEnabled: { type: 'function', def(/*figure*/) { return true; } },
  },

  createRenderers() {
    return [ Plugin.ActiveRenderer() ];
  },

  // [TODO] Work in progress to properly separate Static and Interactive Canvas

  insertViewPlugins( plugins ) {

    const renderPlugins = Config.useSpecialPens ? [ Plugin.SpecialPens() ] : [];

    plugins.insert(0,

      renderPlugins.concat( this.createRenderers() ).concat([

      Plugin.ViewControls(),

      Plugin.PluginsLayer(),

      Plugin.AlignmentLines(),
      Plugin.FigureAnnotations(),

      Plugin.Tools()

    ]));
  }

});



/** WEBPACK FOOTER **
 ** ./src/ui/canvas/canvas.js
 **/